/**
 * DeviceIdentityService — registers this browser with the backend.
 *
 * ADAPTED from BigxBookDesktop/src/services/deviceIdentity.ts. Two changes:
 *   • platform is "WEB" instead of "DESKTOP". No backend change is needed —
 *     Prisma already declares the column as `platform String? // ANDROID, IOS,
 *     WEB` and /auth/register-device accepts it as a free string.
 *   • getDeviceName() is rewritten. Desktop names the device after its
 *     userData path, which does not exist in a browser, so the name is derived
 *     from the browser and OS instead and can be edited by the operator.
 *
 * The device id generator is unchanged: `crypto.getRandomValues` is Web Crypto
 * and works identically here.
 *
 * NOTE ON DEVICE SLOTS (BIGXBOOK_WEB_PLAN §7.3, Decision 5): every browser
 * that registers claims one of a store's 100 slots, permanently. Browsers churn
 * far faster than desktop installs, so slot usage must stay visible. Slots are
 * per-store: one browser used across three stores takes one slot in each store's
 * pool, not three from one.
 */
import { http } from './httpClient';
import { kvStore } from '@/platform';
import { useAppStore } from '@/store/useAppStore';
import { useDeviceRegistrationStore } from './deviceRegistrationStore';

const DEVICE_ID_KEY = 'device.id';
const DEVICE_NAME_KEY = 'device.name';

/** Generates `dev_<32-hex>` matching BigxBook regex. */
function generateDeviceId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `dev_${hex}`;
}

export async function getOrCreateDeviceId(): Promise<string> {
  const existing = await kvStore.getString(DEVICE_ID_KEY);
  if (existing) return existing;
  const fresh = generateDeviceId();
  await kvStore.set(DEVICE_ID_KEY, fresh);
  return fresh;
}

/**
 * Give this browser a new device identity.
 *
 * Only for the case below: the id it is holding belongs to a DIFFERENT
 * merchant's business, so it can never be registered again here.
 */
export async function rotateDeviceId(): Promise<string> {
  const fresh = generateDeviceId();
  await kvStore.set(DEVICE_ID_KEY, fresh);
  await useAppStore.getState().setDeviceId(fresh);
  return fresh;
}

/**
 * Best-effort browser + OS label, e.g. "Chrome on Windows".
 *
 * User-Agent Client Hints are preferred where available; the UA string is a
 * fallback. Both are advisory only — this is a human-readable label shown in
 * the device list, never an identity or security boundary.
 */
function detectDeviceName(): string {
  const uaData = (navigator as Navigator & {
    userAgentData?: { brands?: Array<{ brand: string }>; platform?: string };
  }).userAgentData;

  if (uaData?.brands?.length) {
    // Skip the deliberate junk brand ("Not)A;Brand") the spec injects.
    const brand = uaData.brands.find(
      (b) => !/not.?a.?brand/i.test(b.brand),
    )?.brand;
    if (brand) return `${brand} on ${uaData.platform || 'Web'}`;
  }

  const ua = navigator.userAgent;
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    // Chrome's UA contains "Safari", so Chrome must be ruled out first.
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser';

  const os =
    /Windows/.test(ua) ? 'Windows'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : 'Web';

  return `${browser} on ${os}`;
}

export async function getDeviceName(): Promise<string> {
  const cached = await kvStore.getString(DEVICE_NAME_KEY);
  if (cached) return cached;
  const name = detectDeviceName();
  await kvStore.set(DEVICE_NAME_KEY, name);
  return name;
}

/** Lets the operator rename this device so a counter is identifiable in the list. */
export async function setDeviceName(name: string): Promise<void> {
  await kvStore.set(DEVICE_NAME_KEY, name.trim() || detectDeviceName());
}

export interface RegisterDeviceResponse {
  success: boolean;
  registered: boolean;
  warning?: string;
}

/**
 * True for the one refusal a fresh identity actually fixes.
 *
 * Deliberately narrow. The plan device LIMIT is a different refusal — it comes
 * back 201 with `registered: false` — and rotating the id there would let any
 * till mint itself an unlimited supply of slots.
 */
function isForeignDeviceError(err: unknown): boolean {
  const e = err as { status?: number; body?: { message?: string }; message?: string };
  if (e?.status !== 403) return false;
  const text = `${e.body?.message ?? ''} ${e.message ?? ''}`;
  return /registered to a different merchant/i.test(text);
}

/**
 * One registration at a time.
 *
 * registerDevice() is called from more than one place during start-up
 * (bootstrapStore and the store gate). Normally harmless — the second call
 * re-registers the same id. But on the foreign-device path both callers read
 * the OLD id, both get 403, and both mint a new identity: observed live,
 * leaving an orphan device row with no slot 13ms before the one that won.
 *
 * Harmless today because device limits are off, and exactly the kind of thing
 * that stops being harmless when they are turned on.
 */
let inFlight: Promise<RegisterDeviceResponse> | null = null;

export function registerDevice(): Promise<RegisterDeviceResponse> {
  if (!inFlight) {
    inFlight = registerDeviceOnce().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function registerDeviceOnce(): Promise<RegisterDeviceResponse> {
  const [deviceId, deviceName] = await Promise.all([
    getOrCreateDeviceId(),
    getDeviceName(),
  ]);
  const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0-dev';

  // Publish into useAppStore so the sync engine can read it.
  await useAppStore.getState().setDeviceId(deviceId);

  try {
    const res = await http<RegisterDeviceResponse>('/auth/register-device', {
      method: 'POST',
      body: {
        deviceId,
        deviceName,
        platform: 'WEB',
        deviceType: 'PERSONAL',
        appVersion,
      },
    });
    // The endpoint answers 201 even when it REFUSES (plan device limit), so the
    // body must be inspected rather than the status code. See
    // deviceRegistrationStore for why an unregistered device cannot sync.
    useDeviceRegistrationStore.getState().setResult({
      registered: res.registered !== false,
      warning: res.warning,
    });
    return res;
  } catch (err) {
    // ── A device that used to belong to somebody else's business ──────────
    //
    // The server refuses, correctly: a device row carries a merchant, and one
    // merchant's hardware must not start acting for another. But the refusal
    // left the new shop stranded — registration succeeded, the store was
    // created, and then billing was impossible because no invoice slot is
    // issued without a registered device. The error shown was "connect to the
    // internet once to activate", on a device that was already online. Nothing
    // the shopkeeper could do would fix it.
    //
    // Seen for real on a clean-room run: a machine that had been used by one
    // merchant could never bill for the next one.
    //
    // The identity is just a string this browser generated, so the fix is to
    // generate another. The previous merchant's row keeps its history, and
    // their local data was already unreachable from here the moment a
    // different business signed in — a new id costs nothing that was not
    // already gone.
    if (isForeignDeviceError(err)) {
      const fresh = await rotateDeviceId();
      console.warn(
        `[Device] This browser's device identity belonged to another business. ` +
        `Issued a new one (${fresh.slice(0, 12)}…) so this store can bill.`,
      );
      const retry = await http<RegisterDeviceResponse>('/auth/register-device', {
        method: 'POST',
        body: {
          deviceId: fresh,
          deviceName,
          platform: 'WEB',
          deviceType: 'PERSONAL',
          appVersion,
        },
      });
      useDeviceRegistrationStore.getState().setResult({
        registered: retry.registered !== false,
        warning: retry.warning,
      });
      return retry;
    }

    useDeviceRegistrationStore.getState().setError((err as Error).message);
    throw err;
  }
}
